import { Loader2, Search, Users } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { LoadMoreButton } from "@/components/shared/LoadMoreButton";
import { formatCost, formatNumber } from "@/lib/formatters";
import { adminService } from "@/lib/services/admin";
import type { UserSpendRow } from "@/types";

// ── Search bar ───────────────────────────────────────────────────────────────

interface UserSearchBarProps {
  search: string;
  onSearchChange: (value: string) => void;
}

function UserSearchBar({ search, onSearchChange }: Readonly<UserSearchBarProps>) {
  return (
    <div className="relative">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-content-quaternary" />
      <input
        type="text"
        placeholder="Search users..."
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        className="pl-8 pr-3 py-1.5 w-56 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-1 focus:ring-brand-primary"
      />
    </div>
  );
}

// ── Table row ────────────────────────────────────────────────────────────────

interface UserSpendTableRowProps {
  user: UserSpendRow;
  rank: number;
}

function UserSpendTableRow({ user, rank }: Readonly<UserSpendTableRowProps>) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center py-2 px-1 rounded hover:bg-surface-secondary transition-colors">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs text-content-quaternary w-5 text-right shrink-0">{rank}.</span>
          <div className="min-w-0">
            <p className="text-sm text-content-primary truncate">{user.userName || "Unknown"}</p>
            <p className="text-xs text-content-quaternary truncate">{user.userEmail}</p>
          </div>
        </div>
      </div>
      <span className="text-sm text-content-secondary text-right w-20">{formatNumber(user.requests)}</span>
      <span className="text-sm text-content-secondary text-right w-20">
        {formatNumber(user.tokensInput + user.tokensOutput)}
      </span>
      <span className="text-sm text-content-primary font-medium text-right w-20">{formatCost(user.costUsd)}</span>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

interface AdminUsageByUserProps {
  startDate?: string;
  endDate?: string;
}

const PAGE_SIZE = 10;

export function AdminUsageByUser({ startDate, endDate }: Readonly<AdminUsageByUserProps>) {
  const [users, setUsers] = useState<UserSpendRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Debounce search
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const loadUsers = useCallback(
    async (offset = 0, append = false) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }

        const res = await adminService.getUsageByUser({
          start_date: startDate,
          end_date: endDate,
          search: debouncedSearch || undefined,
          limit: PAGE_SIZE,
          offset,
        });

        const data = res.data;
        if (append) {
          setUsers((prev) => [...prev, ...data.users]);
        } else {
          setUsers(data.users);
        }
        setTotalCount(data.totalCount);
        setHasMore(data.hasMore);
      } catch (err) {
        console.error("Failed to load user spend:", err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [startDate, endDate, debouncedSearch],
  );

  useEffect(() => {
    loadUsers(0, false);
  }, [loadUsers]);

  const handleLoadMore = () => {
    loadUsers(users.length, true);
  };

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-content-tertiary" />
          <h3 className="font-medium text-content-primary">Usage by User</h3>
          {!loading && (
            <span className="text-xs text-content-quaternary">
              ({totalCount} user{totalCount !== 1 ? "s" : ""})
            </span>
          )}
        </div>
        <UserSearchBar search={search} onSearchChange={setSearch} />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
        </div>
      )}
      {!loading && users.length === 0 && (
        <p className="text-center text-content-tertiary py-8">
          {debouncedSearch ? "No users match your search" : "No usage data available"}
        </p>
      )}
      {!loading && users.length > 0 && (
        <>
          {/* Table Header */}
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 text-xs text-content-quaternary uppercase tracking-wider pb-2 border-b border-border-secondary mb-2 px-1">
            <span>User</span>
            <span className="text-right w-20">Requests</span>
            <span className="text-right w-20">Tokens</span>
            <span className="text-right w-20">Cost</span>
          </div>

          {/* Rows */}
          <div className="space-y-0.5">
            {users.map((user, index) => (
              <UserSpendTableRow key={user.userId} user={user} rank={index + 1} />
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <LoadMoreButton
              loadingMore={loadingMore}
              remaining={totalCount - users.length}
              onLoadMore={handleLoadMore}
            />
          )}
        </>
      )}
    </div>
  );
}
